/**
 * Custom settings menu that automatically translates the menu label
 * using media-chrome's i18n system
 */

import { t } from 'media-chrome/utils/i18n.js';
import type { TranslateKeys } from '../lang/en.js';
import { MediaSettingsMenu } from 'media-chrome/menu/media-settings-menu.js';
import { getMediaController } from 'media-chrome/utils/element-utils.js';

class BunnySettingsMenu extends MediaSettingsMenu {
  /**
   * Returns the bunny-settings-menu-button as anchor element
   */
  override get anchorElement(): HTMLElement {
    if (this.anchor !== 'auto') return super.anchorElement;
    const anchor = getMediaController(this)?.querySelector<HTMLElement>(
      'bunny-settings-menu-button',
    );
    if (!anchor) {
      throw new Error('bunny-settings-menu-button not found');
    }
    return anchor;
  }
  static override get observedAttributes() {
    return [...(MediaSettingsMenu.observedAttributes || []), 'translate-key'];
  }

  override connectedCallback() {
    super.connectedCallback();
    // Delay translation until after shadow DOM is fully initialized
    requestAnimationFrame(() => this.#updateTranslation());
  }

  override attributeChangedCallback(
    attrName: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (attrName === 'translate-key' && oldValue !== newValue) {
      this.#updateTranslation();
    }
  }

  #updateTranslation() {
    const key = this.getAttribute('translate-key');
    if (!key) return;

    const translated = t(key as TranslateKeys);

    // Update aria-label
    this.setAttribute('aria-label', translated);
  }
}

if (!globalThis.customElements.get('bunny-settings-menu')) {
  globalThis.customElements.define('bunny-settings-menu', BunnySettingsMenu);
}

export default BunnySettingsMenu;
